import { Dispatch, SetStateAction, useEffect, useState } from "react";
import ModalV2 from "../modal-v2";
import { Box, Stack, Typography, Divider } from "@mui/material";
import { preview_image } from "@/constants";
import { PictureUploader, SelectBox } from "@/components";
import MultiCheckbox from "@/components/checkbox/multiple";
import { useGetTenantAllowedPagesQuery, useSetTenantAllowedPagesMutation } from "@/graphql/page-access";
import { tenantManagementApi, useCreateTenantMutation, useUpdateCompanyInfoMutation } from "@/services";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSnackbar, useUploadFile } from "@/hooks";
import { StyledTextInput } from "./styles";
import { ValidationPatterns } from "@/utils/form-validation";
import { useAppDispatch } from "@/store";

type AddTenantModalProps = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  selectedTenant: ITenant | null;
};

const AddTenantModal = ({
  show,
  setShow,
  selectedTenant,
}: AddTenantModalProps) => {
  const { control, handleSubmit, reset, setValue, watch } = useForm<TenantFormValues>({
    defaultValues: {
      firstName: "",
      email: "",
      companyName: "",
      companyEmail: "",
      companyLogo: null,
      businessAddress: "",
      tenantType: "SERVICE_PROVIDER",
    },
  });
  const dispatch = useAppDispatch();

  const [profilePreview, setProfilePreview] = useState<string | undefined>(
    undefined
  );
  const { isLoading: profileImgLoading, uploadFile } = useUploadFile();

  const { show: showSnackBar } = useSnackbar();

  const [addTenant, { isLoading }] = useCreateTenantMutation();
  const [updateCompanyInfo, { isLoading: isUpdateLoading }] = useUpdateCompanyInfoMutation();
  const [allowedPages, setAllowedPages] = useState<string[]>([]);
  const { data: tenantPages } = useGetTenantAllowedPagesQuery(
    { tenantId: selectedTenant?._id },
    { skip: !selectedTenant?._id }
  );
  const [setTenantAllowedPages] = useSetTenantAllowedPagesMutation();
  const tenantTypeValue = watch("tenantType");

  const handleProfileChange = (file: File | null) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValue("companyLogo", file);
      setProfilePreview(fileURL);
    }
  };

  const handleCreateTenant: SubmitHandler<TenantFormValues> = async (data) => {
    const {
      firstName,
      email,
      companyName,
      companyEmail,
      companyLogo,
      businessAddress,
      tenantType,
    } = data;

    let companyLogoKey;
    if (companyLogo) {
      const data = await uploadFile(companyLogo as File);
      companyLogoKey = data?.key || "";
    }
    const tenantData = {
      firstName,
      email,
      companyName,
      companyEmail,
      companyLogo: companyLogoKey || "",
      businessAddress,
      tenantType,
    };

    try {
      if (selectedTenant?._id) {
        await updateCompanyInfo({
          tenantData: {
            businessAddress,
            companyName,
            companyLogo: companyLogoKey,
          },
          tenantId: selectedTenant?._id,
        }).unwrap();
        if (allowedPages && allowedPages.length) {
          await setTenantAllowedPages({ tenantId: selectedTenant._id, pages: allowedPages }).unwrap();
        }
        dispatch(tenantManagementApi.util.invalidateTags([{ type: "tenantManagement" }]));
      } else {
        await addTenant({
          tenantData: { ...tenantData, allowedPages },
        }).unwrap();
      }
      showSnackBar(
        selectedTenant?._id ? "Tenant Updated Successfully!" : "Client Created Successfully!",
        "",
        "success"
      );
      setShow(false);
      reset();
    } catch (error: any) {
      showSnackBar(error.message, "", "error");
    }
  };

  useEffect(() => {
    if (selectedTenant?._id) {
      setProfilePreview(selectedTenant?.companyLogo || "");
      setValue("companyName", selectedTenant?.companyName);
      setValue("companyEmail", selectedTenant?.companyEmail);
      setValue("businessAddress", selectedTenant?.businessAddress || "");
    }
  }, [selectedTenant?._id]);

  useEffect(() => {
    if (tenantPages?.pages) setAllowedPages(tenantPages.pages);
  }, [tenantPages]);

  return (
    <ModalV2
      show={show}
      setShow={setShow}
      buttonAction={handleSubmit(handleCreateTenant)}
      isConfirmButtonLoading={isLoading || profileImgLoading || isUpdateLoading}
      buttonText={"Submit"}
      title="Add New Tenant"
      subTitle="They'll be able to access Riffs and see their data once invited"
    >
      <Box component="form" onSubmit={handleSubmit(handleCreateTenant)}>
        <Stack my={2}>
          {!selectedTenant?._id && (
            <>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field: { onChange, ...rest }, fieldState }) => (
                  <StyledTextInput
                    label="First Name"
                    type="text"
                    {...rest}
                    placeholder="Name"
                    handleChangeValue={onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: ValidationPatterns.email,
                    message: "Invalid email address",
                  },
                }}
                render={({ field: { onChange, ...rest }, fieldState }) => (
                  <StyledTextInput
                    label="Email Address"
                    {...rest}
                    type="text"
                    placeholder="h1@example.com"
                    handleChangeValue={onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="tenantType"
                control={control}
                rules={{ required: "Tenant type is required" }}
                render={({
                  field: { onChange, value, ...rest },
                  fieldState,
                }) => (
                  <SelectBox
                    label="Tenant Type"
                    placeholder="Select tenant type"
                    value={value}
                    handleChangeValue={onChange}
                    sx={{
                      mb: 2,
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    options={[
                      { value: "SERVICE_PROVIDER", label: "Service Provider" },
                      { value: "TRADEDIVE_CLIENT", label: "Riffs Client" },
                    ]}
                    {...rest}
                  />
                )}
              />
            </>
          )}
          <Typography variant="h5" mt={1}>
            Company Details
          </Typography>
          <PictureUploader
            title="Company Logo"
            description="We recommend an image of at least 400x400"
            imgUrl={profilePreview ? profilePreview : preview_image}
            customPreviewType={profilePreview ? "rounded" : "default"}
            onChange={handleProfileChange}
          />
          <Controller
            name="companyName"
            control={control}
            rules={{ required: "Company name is required" }}
            render={({ field: { onChange, ...rest }, fieldState }) => (
              <StyledTextInput
                label="Company Name"
                type="text"
                {...rest}
                placeholder="Company Name"
                handleChangeValue={onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="companyEmail"
            control={control}
            rules={{
              required: "Company email is required",
              pattern: {
                value: ValidationPatterns.email,
                message: "Invalid email address",
              },
            }}
            render={({ field: { onChange, ...rest }, fieldState }) => (
              <StyledTextInput
                label="Company Email Address"
                {...rest}
                disabled={!!selectedTenant?._id}
                type="text"
                placeholder="company@example.com"
                handleChangeValue={onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="businessAddress"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <>
                <StyledTextInput
                  label="Company Address"
                  {...rest}
                  type="text"
                  placeholder="Company Address"
                  handleChangeValue={onChange}
                />
              </>
            )}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="h5" mt={1}>Pages</Typography>
          <Typography sx={{ color: "#8E9494", fontSize: "12px", mb: 1 }}>
            Select which app pages are enabled for this tenant by default
          </Typography>
          <MultiCheckbox
            label="Core Pages"
            checkboxes={[
              { label: "Analytics" },
              { label: "Potential Savings" },
              { label: "Scenario Modeling" },
              { label: "Chat Assistance" },
              { label: "Product Hub" },
              ...(((selectedTenant?._id ? selectedTenant?.tenantType === 'SERVICE_PROVIDER' : tenantTypeValue === 'SERVICE_PROVIDER')) ? [{ label: 'Product Hub Duty Columns' }] : []),
              { label: "Trade Data" },
              { label: "Documents" },
              { label: "Post Import Adjustment" },
            ]}
            checked={[
              "ANALYTICS",
              "POTENTIAL_SAVINGS",
              "SCENARIO_MODELING",
              "CHAT_ASSISTANCE",
              "PRODUCT_HUB",
              ...(((selectedTenant?._id ? selectedTenant?.tenantType === 'SERVICE_PROVIDER' : tenantTypeValue === 'SERVICE_PROVIDER')) ? ['PRODUCT_HUB_DUTY_COLUMNS'] : []),
              "TRADE_DATA",
              "DOCUMENTS",
              "POST_IMPORT_ADJUSTMENT",
            ].map(k=>allowedPages.includes(k))}
            handleParentChange={(e)=>{
              const checked = e.target.checked;
              const map = [
                "ANALYTICS",
                "POTENTIAL_SAVINGS",
                "SCENARIO_MODELING",
                "CHAT_ASSISTANCE",
                "PRODUCT_HUB",
                ...(((selectedTenant?._id ? selectedTenant?.tenantType === 'SERVICE_PROVIDER' : tenantTypeValue === 'SERVICE_PROVIDER')) ? ['PRODUCT_HUB_DUTY_COLUMNS'] : []),
                "TRADE_DATA",
                "DOCUMENTS",
                "POST_IMPORT_ADJUSTMENT",
              ];
              if (checked) {
                setAllowedPages(map);
              } else {
                setAllowedPages([]);
              }
            }}
            handleChildChange={(index)=>()=>{
              const map = [
                "ANALYTICS",
                "POTENTIAL_SAVINGS",
                "SCENARIO_MODELING",
                "CHAT_ASSISTANCE",
                "PRODUCT_HUB",
                ...(((selectedTenant?._id ? selectedTenant?.tenantType === 'SERVICE_PROVIDER' : tenantTypeValue === 'SERVICE_PROVIDER')) ? ['PRODUCT_HUB_DUTY_COLUMNS'] : []),
                "TRADE_DATA",
                "DOCUMENTS",
                "POST_IMPORT_ADJUSTMENT",
              ];
              const key = map[index];
              setAllowedPages(prev => prev.includes(key) ? prev.filter(k=>k!==key) : [...prev, key]);
            }}
          />
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default AddTenantModal;
